import Grid, { Grid2Props } from "@mui/material/Unstable_Grid2";
import { Controller, useFormContext } from "react-hook-form";
import { Box, Stack } from "@mui/material";
import { DeleteOutlined, Download } from "@mui/icons-material";
import { ExceptionTypes, exceptionTypeOptions } from "src/models/staffPriceExceptions/StaffPriceExceptionTypes";
import Radio from "@jbhi-fi/lanyard-ui/components/Radio/Radio";
import { ProductSelect } from "src/pages/Messaging/components/ProductSelect";
import { AutoCompleteType, InputType } from "@jbhi-fi/lanyard-ui/components/TextField/TextField.types";
import TextField from "@jbhi-fi/lanyard-ui/components/TextField/TextField";
import { Button } from "src/components/Form";
import { getConsts } from "src/utils/consts";
import { useHistory } from "react-router-dom";
import { CreateExceptionModel, skuExceptionSchema } from "src/validationSchemas/createExceptionSchema";
import { useState, useRef } from "react";
import { useContext } from "react";
import PageContext from "src/components/Context/PageContext";
import { DataGrid, GridToolbarContainer, GridToolbarExport, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import Papa from "papaparse";
import { hasPermission } from "src/infrastructure/auth/PermissionUtils";
import { UserRoles } from "src/infrastructure/auth/UserRoles";

const gridBorder = {
    border: 1,
    borderColor: "grey.400",
} as Grid2Props;

const gridSpacing = {
    paddingY: 4,
    paddingX: 10,
    marginTop: 3,
    marginX: 0,
    marginBottom: 0,
    spacing: 3,
} as Grid2Props;

type ExceptionFormProps = {
    onFormSubmit: (data: CreateExceptionModel) => void;
    onFormError: (data: any) => void
}

export interface SkuException {
  type: ExceptionTypes[];
  isActive: boolean;
  details: string[];
  price: number;
  authorisedBy: string;
  comment: string;
}

export const ExceptionForm: React.FC<ExceptionFormProps> = (props: ExceptionFormProps) => {
    const history = useHistory();
    const formContext = useFormContext();
    const exceptionTypesWatch = formContext.watch("type") as ExceptionTypes[] || [ExceptionTypes.Sku];
    const [contextData] = useContext(PageContext);
    const { userProfile } = contextData;
    const [values, setValues] = useState<SkuException[]>([]);
    const [uploadErrors, setUploadErrors] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const columns: GridColDef[] = [
        { 
            field: "details", 
            headerName: "SKU", 
            width: 100,
            valueGetter: (params) => {
                const details = params.row.details;
                return Array.isArray(details) ? details.join(", ") : details;
            }
        },
        {
            field: "price",
            headerName: "Price",
            width: 100,
        },
        { 
            field: "authorisedBy", 
            headerName: "Authorised By", 
            width: 200 
        },
        { 
            field: "comment", 
            headerName: "Comment", 
            width: 525 
        },
        {
          field: "actions",
          headerName: "Action",
          width: 100,
          renderCell: (params: GridRenderCellParams) => {
            const sku = Array.isArray(params.row.details) ? params.row.details[0] : params.row.details;
            return (
                <Button
                    type={"iconOnly"}
                    icon={<DeleteOutlined/>}
                    onClick={() => handleRemoveRow(sku)}
                />
            );
          }
        }
    ];

    function CustomToolbar() {
        return <GridToolbarContainer>{<GridToolbarExport />}</GridToolbarContainer>;
    };

    const handleRemoveRow = (sku: string) => {
        setValues(prevValues => prevValues.filter(item => 
            // Compare the first detail value (SKU) to the target SKU
            !(item.details && Array.isArray(item.details) && item.details[0] === sku)
        ));
    };

    const onAddClick = async () => {
      const formValues = formContext.getValues();
      
      // Check if multiple SKUs are selected
      const skuDetails = Array.isArray(formValues.details) ? formValues.details : [formValues.details];
      
      try {
        const baseEntry = {
          type: exceptionTypesWatch,
          isActive: true,
          price: formValues.price,
          authorisedBy: userProfile.displayName,
          comment: formValues.comment,
        };
        
        // Validate one entry to check if the form is valid
        await skuExceptionSchema.validate({
          ...baseEntry, 
          details: [skuDetails[0]]
        }, { abortEarly: false });
        
        // If valid, create separate entries for each SKU
        const newEntries = skuDetails.map(sku => ({
          ...baseEntry,
          details: [sku],
        }));
        
        // Add all new entries to the values array
        handleAddSkus(newEntries);
        
        // Reset form fields except for type
        formContext.reset({
            type: exceptionTypesWatch,
            isActive: true,
            details: "",
            price: "",
            authorisedBy: userProfile.displayName,
            comment: "",
        });
        formContext.clearErrors();
      } catch (validationError: any) {
        if (validationError.inner) {
          validationError.inner.forEach((err) => {
            formContext.setError(err.path, {
              type: "manual",
              message: err.message,
            });
          });
      
          props.onFormError(validationError.inner);
        }
      }
    };
    
    const onCancelClick = () => {
        const consts = getConsts();
        history.push(consts.routerPath.staffExceptions);
    };

    const onSaveClick = () => {
        const data: CreateExceptionModel = {
          skuExceptions: values,
        };
        props.onFormSubmit(data);
    };

    const handleRadioChange = (value: string) => {
        formContext.reset({
            type: [value], 
            isActive: true,
            details: "",         
            price: undefined,         
            authorisedBy: userProfile.displayName,         
            comment: "",             
        });
    };

    const handleAddSkus = (newEntries) => {
        if (newEntries && newEntries.length > 0) {
            setValues(prevValues => [...prevValues, ...newEntries]);
        } else {
            console.error("SKU Exception is not set or invalid.");
        }
    };

    const validateCsvData = (data: any[]): { valid: SkuException[], errors: string[] } => {
        const validEntries: SkuException[] = [];
        const errors: string[] = [];
        
        data.forEach((row, index) => {
            const rowNumber = index + 2;
            const rowErrors: string[] = [];
            
            // Normalize headers
            const normalizedRow: any = {};
            Object.keys(row).forEach(key => {
                const normalizedKey = key.trim().toLowerCase();
                normalizedRow[normalizedKey] = typeof row[key] === 'string' ? row[key].trim() : row[key];
            });
            
            // Check for required fields
            const sku = normalizedRow['sku'];
            const price = normalizedRow['price'];
            const comment = normalizedRow['comment'];
            
            if (!sku || sku === '') {
                rowErrors.push(`Row ${rowNumber}: SKU is required`);
            }
            
            if (!price || price === '') {
                rowErrors.push(`Row ${rowNumber}: Price is required`);
            } else {
                const numericPrice = parseFloat(price);
                if (isNaN(numericPrice)) {
                    rowErrors.push(`Row ${rowNumber}: Price must be a number`);
                } else if (numericPrice < 0) {
                    rowErrors.push(`Row ${rowNumber}: Price cannot be negative`);
                }
            }
            
            if (!comment || comment === '') {
                rowErrors.push(`Row ${rowNumber}: Comment is required`);
            }
            
            if (rowErrors.length > 0) {
                errors.push(...rowErrors);
            } else {
                // Create valid entry
                validEntries.push({
                    type: exceptionTypesWatch,
                    isActive: true,
                    details: [sku],
                    price: parseFloat(price),
                    authorisedBy: userProfile.displayName,
                    comment: comment
                });
            }
        });
        
        return { valid: validEntries, errors };
    };

    const processCsvFile = (file: File) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            dynamicTyping: false, // Keep as strings for validation
            complete: (results) => {
                if (results.errors && results.errors.length > 0) {
                    const parseErrors = results.errors.map(error => 
                        `Parse error at row ${error.row}: ${error.message}`
                    );
                    setUploadErrors(parseErrors);
                    return;
                }
                
                if (!results.data || results.data.length === 0) {
                    setUploadErrors(['CSV file is empty or contains no valid data']);
                    return;
                }
                
                // Check for required headers
                const firstRow = results.data[0] as any;
                const headers = Object.keys(firstRow).map(h => h.trim().toLowerCase());
                const requiredHeaders = ['sku', 'price', 'comment'];
                const missingHeaders = requiredHeaders.filter(header => !headers.includes(header));
                
                if (missingHeaders.length > 0) {
                    setUploadErrors([`Missing required headers: ${missingHeaders.join(', ')}`]);
                    return;
                }
                
                const { valid, errors } = validateCsvData(results.data as any[]);
                
                if (errors.length > 0) {
                    setUploadErrors(errors);
                    return;
                }
                
                if (valid.length === 0) {
                    setUploadErrors(['No valid entries found in CSV']);
                    return;
                }
                
                // Clear any previous errors and add valid entries
                setUploadErrors([]);
                setValues(prevValues => [...prevValues, ...valid]);
                
                console.log(`Successfully uploaded ${valid.length} entries from CSV`);
            },
            error: (error) => {
                setUploadErrors([`Failed to parse CSV: ${error.message}`]);
            }
        });
    };

    const resetValues = () => {
        setValues([]);
        setUploadErrors([]);
    };

    const downloadTemplate = async () => {
        try {
            const response = await fetch('/staff-price-exceptions-template.csv');
            
            if (!response.ok) {
                throw new Error(`Failed to fetch template: ${response.status} ${response.statusText}`);
            }
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'staff-price-exceptions-template.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            console.log("Template downloaded successfully");
        } catch (error) {
            console.error("Error downloading template:", error);
            // Optionally show user-friendly error message
            alert("Failed to download template. Please try again or contact support.");
        }
    };

    const bulkUpload = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        if (!file.name.toLowerCase().endsWith('.csv')) {
            setUploadErrors(['Please select a CSV file']);
            return;
        }
        
        if (file.size > 5 * 1024 * 1024) { // 5MB limit
            setUploadErrors(['File size must be less than 5MB']);
            return;
        }
        
        processCsvFile(file);
        
        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    if (!hasPermission(UserRoles.PricingStaffExceptions, userProfile.roles)) {
        window.location.href = "/";
        return null;
    }
    

    return (
        <form>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".csv"
                style={{ display: 'none' }}
            />
            
            <Grid container {...gridBorder} {...gridSpacing}>
                <Grid xs={12}>
                    <h2>Staff exception details</h2>
                </Grid>

                {uploadErrors.length > 0 && (
                    <Grid xs={12}>
                        <Box sx={{ 
                            backgroundColor: '#ffebee', 
                            border: '1px solid #f44336', 
                            borderRadius: 1, 
                            padding: 2, 
                            marginBottom: 2 
                        }}>
                            <h4 style={{ margin: '0 0 8px 0', color: '#d32f2f' }}>Upload Errors:</h4>
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {uploadErrors.map((error, index) => (
                                    <li key={index} style={{ color: '#d32f2f' }}>{error}</li>
                                ))}
                            </ul>
                        </Box>
                    </Grid>
                )}

                <Grid xs={12}>
                    <label>Exception type:</label>
                    <Controller
                        name="type"
                        control={formContext.control}
                        render={({ field }) => (
                            <Stack display="inline-flex" direction="row" spacing={3} marginLeft={3}>
                                {exceptionTypeOptions.map(({ value, label }) => (
                                    <Radio
                                        key={`${field.name}-${value}`}
                                        id={`${field.name}-${value}`}
                                        name={field.name}
                                        label={label}
                                        value={value}
                                        onChange={(e) => handleRadioChange(e.target.value)}
                                        checked={exceptionTypesWatch.includes(value)}
                                    />
                                ))}
                            </Stack>
                        )}
                    />
                </Grid>

                {exceptionTypesWatch.includes(ExceptionTypes.Sku) && (
                    <>
                    <Grid xs={12}>
                        <label>SKU</label>
                        <Controller
                            name="details"
                            control={formContext.control}
                            render={({ field }) => {
                                const detailsError = formContext.formState.errors?.details;
                                let errorMessage = '';
                                
                                if (detailsError) {
                                    if (typeof detailsError.message === 'string') {
                                        // Direct message on the array
                                        errorMessage = detailsError.message;
                                    } else if (Array.isArray(detailsError) && detailsError[0]?.message) {
                                        // Message on first array element
                                        errorMessage = detailsError[0].message;
                                    } else if (detailsError.message) {
                                        // Fallback to message property
                                        errorMessage = detailsError.message.toString();
                                    }
                                }
                                
                                return (
                                    <ProductSelect
                                        name={field.name}
                                        value={field.value ?? []}
                                        onChange={field.onChange}
                                        hasError={!!detailsError}
                                        errorMessage={errorMessage}
                                        showBulkUpload={false}
                                        showExport={false}
                                    />
                                );
                            }} 
                        />
                    </Grid>

                    <Grid xs={6}>
                        <label>SKU Price</label>
                        <Controller
                            name="price"
                            control={formContext.control}
                            render={({ field }) => {
                                const handleChange = (e) => {
                                    field.onChange(e);
                                    // Clear error when field has value
                                    if (e.target.value) {
                                        formContext.clearErrors('price');
                                    }
                                };
                                
                                return (
                                    <TextField
                                        id={field.name}
                                        name={field.name}
                                        value={field.value}
                                        onChange={handleChange}
                                        type={InputType.number}
                                        min="0"
                                        autoComplete={AutoCompleteType.off}
                                        hasError={!!formContext.formState.errors?.price}
                                        errorMsg={formContext.formState.errors.price?.message?.toString() || ''}
                                    />
                                );
                            }} 
                        />
                    </Grid>

                    </>
                )}

                <Grid xs={6}>
                    <label>Comment</label>
                    <Controller
                        name="comment"
                        control={formContext.control}
                        render={({ field }) => {
                            const handleChange = (e) => {
                                field.onChange(e);
                                // Clear error when field has value
                                if (e.target.value.trim()) {
                                    formContext.clearErrors('comment');
                                }
                            };
                            
                            return (
                                <TextField
                                    id={field.name}
                                    name={field.name}
                                    value={field.value}
                                    onChange={handleChange}
                                    autoComplete={AutoCompleteType.off}
                                    hasError={!!formContext.formState.errors?.comment}
                                    errorMsg={formContext.formState.errors.comment?.message?.toString() || ''}
                                />
                            );
                        }}
                    />
                </Grid>
                
                <Grid xs={12} display="flex" justifyContent="space-between">
                    <Button type="secondary" onClick={resetValues}>
                    Clear
                    </Button>
                    <Button
                        type="secondary"
                        icon={<Download />}
                        onClick={downloadTemplate}
                    >
                        Template
                    </Button>
                    <Button
                        type="primary"
                        onClick={bulkUpload}
                    >
                        Bulk Upload
                    </Button>
                    <Button 
                        type="action" 
                        onClick={onAddClick}
                    >
                        Add
                    </Button>
                </Grid>
            </Grid>

            <Box sx={{ height: "400px", width: "100%", mt: 4 }}>
                <DataGrid
                    density="compact"
                    components={{ Toolbar: CustomToolbar }} 
                    componentsProps={{
                        toolbar: {
                            csvOptions: {
                                fields: ["details", "price", "authorisedBy", "comment"],
                            },
                        },
                    }}
                    rows={values}
                    getRowId={() => crypto.randomUUID()}
                    sx={{ padding: "0px 32px", maxWidth: "1400px", margin: "auto" }}
                    columns={columns} 
                    pageSize={100}
                    rowsPerPageOptions={[100]}
                    disableSelectionOnClick
                    experimentalFeatures={{ newEditingApi: true }}
                    // hideFooterPagination={true}
                />
            </Box>

            <Grid container marginTop={3}>
                <Grid xs={12} display="flex" justifyContent="space-between">
                    <Button type="link" onClick={onCancelClick}>
                    Cancel
                    </Button>
                    <Button 
                        type="primary" 
                        onClick={onSaveClick} 
                        disabled={values.length === 0}>
                    Save
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};